"use client"

import { useState, useEffect, useCallback } from "react"
import { Heart, Loader2 } from "lucide-react"
import { Textarea } from "@/components/ui/textarea"
import { Button } from "@/components/ui/button"
import { motion, AnimatePresence } from "framer-motion"
import { getNowInPrague } from "@/lib/timezone"

interface GratitudeChallengeProps {
  isAnonymous?: boolean
}

export function GratitudeChallenge({ isAnonymous = false }: GratitudeChallengeProps) {
  const [show, setShow] = useState(false)
  const [gratitude, setGratitude] = useState("")
  const [saving, setSaving] = useState(false)

  // Get the date for the current gratitude window
  // Between midnight and 3 AM, we're still in yesterday's window
  const getGratitudeDate = useCallback(() => {
    const now = getNowInPrague()
    const hour = now.getHours()
    if (hour < 3) {
      const yesterday = new Date(now)
      yesterday.setDate(yesterday.getDate() - 1)
      return yesterday.toISOString().split("T")[0]
    }
    return now.toISOString().split("T")[0]
  }, [])

  // Check if in time window and if entry already exists
  useEffect(() => {
    if (typeof window === "undefined" || isAnonymous) return

    const hour = getNowInPrague().getHours()
    // Show only between 18:00 and 03:00
    const isInTimeWindow = hour >= 18 || hour < 3

    if (!isInTimeWindow) {
      setShow(false)
      return
    }

    const checkExistingEntry = async () => {
      try {
        const response = await fetch("/api/gratitude")
        if (!response.ok) {
          setShow(true) // Show if we can't check
          return
        }

        const data = await response.json()
        const gratitudeDate = getGratitudeDate()

        const existingEntry = data.entries?.find(
          (entry: { date: string }) => entry.date === gratitudeDate
        )

        // Hide if entry already exists for this window
        setShow(!existingEntry)
      } catch (error) {
        console.error("Failed to check gratitude entry:", error)
        setShow(true) // Show if we can't check
      }
    }

    checkExistingEntry()
  }, [isAnonymous, getGratitudeDate])

  const handleSave = async () => {
    if (!gratitude.trim() || saving || isAnonymous) return

    setSaving(true)
    try {
      const response = await fetch("/api/gratitude", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          date: getGratitudeDate(),
          content: gratitude.trim(),
        }),
      })

      if (response.ok) {
        // Hide after successful save
        setShow(false)
      }
    } catch (error) {
      console.error("Failed to save gratitude entry:", error)
    } finally {
      setSaving(false)
    }
  }

  if (isAnonymous) return null

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          className="rounded-xl p-4 sm:p-5 border bg-gradient-to-br from-pink-50/80 to-rose-50/60 border-pink-200/50"
        >
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 mt-0.5">
                <Heart className="h-5 w-5 text-pink-500" />
              </div>
              <div>
                <h3 className="font-medium text-pink-900">
                  Večerní výzva 🙏
                </h3>
                <p className="text-sm text-pink-700/80 mt-1">
                  Zkus si zapsat 3 věci, za které jsi dnes vděčný/á
                </p>
              </div>
            </div>
            <Textarea
              value={gratitude}
              onChange={(e) => setGratitude(e.target.value)}
              placeholder=""
              className="min-h-[100px] bg-white/70 border-pink-200/60 focus:border-pink-300 focus:ring-pink-200 placeholder:text-pink-300 resize-none"
            />
            <div className="flex items-center justify-between">
              <p className="text-xs text-pink-600/60">
                Uloží se do tvé historie
              </p>
              <Button
                size="sm"
                variant="outline"
                onClick={handleSave}
                disabled={!gratitude.trim() || saving}
                className="border-pink-300 text-pink-700 hover:bg-pink-100/50 hover:text-pink-800 disabled:opacity-50"
              >
                {saving ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                    Ukládám...
                  </>
                ) : (
                  "Uložit"
                )}
              </Button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
